import React from 'react';
import clsx from 'clsx';
import * as BizCharts from 'bizcharts';
import './registerComparisonConnectorShape';

const { Chart, Axis, Geom, Legend, Tooltip, View } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const DEFAULT_HEIGHT = 430;
const DEFAULT_APPEND_PADDING: [number, number, number, number] = [57, 36, 40, 16];

export type PerformanceScatterChartProps = {
	data: any[];
	xField: string;
	yField: string;
	xAxisTitle: string;
	yAxisTitle: string;
	formatAxisLabel: (val: any) => string;
	/** BizCharts `color` prop value for the point geom (string or [fields, fn]). */
	pointColor: any;
	/** BizCharts `shape` prop value for the point geom (string or [fields, fn]). */
	pointShape: any;
	/** BizCharts `size` prop value for the point geom (number or [fields, fn]). */
	pointSize: any;
	/** BizCharts `style` prop value for the point geom (object or [fields, fn]). */
	pointStyle: any;
	/** Tooltip definition `[fields, formatter]` for the point geom. */
	pointTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }];
	onPointClick?: (ev: any) => void;
	/** Additional scale config (e.g., performerGroup values order, nice scaling on axes). */
	scale?: Record<string, any>;
	pointLabel?: any;
	edges?: {
		data: any[];
		style?: any;
	};
	onGetG2Instance?: (chart: any) => void;
	tooltipDomStyles?: Record<string, React.CSSProperties>;
	height?: number;
	appendPadding?: [number, number, number, number];
};

const ITEM_TPL = '<li data-index={index} class="m-0 list-none p-0">{value}</li>';

const HIDDEN_AXIS_PROPS = {
	visible: false,
	grid: null as any,
	line: null as any,
	label: null as any,
	tickLine: null as any,
	title: null as any,
};

const buildAxisProps = (
	name: string,
	title: string,
	offset: number,
	formatAxisLabel: (val: any) => string
) => ({
	name,
	title: {
		text: title,
		style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
		offset,
	},
	label: {
		formatter: formatAxisLabel,
		style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
	},
	line: { style: { stroke: AXIS_Y_LINE_COLOR } },
	tickLine: null as any,
	grid: {
		line: {
			style: {
				stroke: AXIS_Y_LINE_COLOR,
				lineDash: [4, 4],
			},
		},
	},
});

const PerformanceScatterChart = ({
	data,
	xField,
	yField,
	xAxisTitle,
	yAxisTitle,
	formatAxisLabel,
	pointColor,
	pointShape,
	pointSize,
	pointStyle,
	pointTooltipDefinition,
	onPointClick,
	scale,
	pointLabel,
	edges,
	onGetG2Instance,
	tooltipDomStyles,
	height = DEFAULT_HEIGHT,
	appendPadding = DEFAULT_APPEND_PADDING,
}: PerformanceScatterChartProps) => {
	const xAxis = buildAxisProps(xField, xAxisTitle, 40, formatAxisLabel);
	const yAxis = buildAxisProps(yField, yAxisTitle, 70, formatAxisLabel);
	const hasComparisonEdges = !!(edges && edges.data && edges.data.length > 0);

	return (
		<div
			className={clsx(
				'analytics-g2-chart w-full min-w-0',
				hasComparisonEdges ? 'analytics-g2-chart--comparison' : 'analytics-g2-chart--single'
			)}
		>
			<Chart
				key={hasComparisonEdges ? 'comparison' : 'single'}
				height={height}
				appendPadding={appendPadding}
				data={data}
				autoFit={true}
				forceFit={true}
				theme="openwrench"
				scale={scale}
				onGetG2Instance={onGetG2Instance}
				onPointClick={onPointClick}
			>
				<Axis {...xAxis} />
				<Axis {...yAxis} />
				<Legend visible={false} />
				<Tooltip
					showTitle={false}
					crosshairs={false}
					itemTpl={ITEM_TPL}
					domStyles={tooltipDomStyles}
				/>
				{hasComparisonEdges ? (
					<View data={edges.data} scale={scale} padding={0}>
						<Axis name={xField} {...HIDDEN_AXIS_PROPS} />
						<Axis name={yField} {...HIDDEN_AXIS_PROPS} />
						<Geom
							type="edge"
							shape="comparison-connector"
							position={`${xField}*${yField}`}
							style={edges.style}
							tooltip={false}
							animate={{ update: false }}
						/>
					</View>
				) : null}
				<Geom
					type="point"
					position={`${xField}*${yField}`}
					color={pointColor}
					shape={pointShape}
					size={pointSize}
					style={pointStyle}
					tooltip={pointTooltipDefinition}
					animate={{ update: false }}
					{...(pointLabel ? { label: pointLabel } : {})}
				/>
			</Chart>
		</div>
	);
};

export default PerformanceScatterChart;
